import { useRef, useState } from 'react';
import { uniqBy } from 'lodash';
import styled from 'styled-components';
import { ButtonReset, InlineList, Media, MediaBody, MediaImage } from 'ui';
import { MdRemoveCircleOutline } from 'react-icons/md';
import { Input as BorderedInput } from 'components/TextField';
import { useTranslation } from 'react-i18next';
import { useDeepCompareEffect, useEffectOnce } from 'react-use';
import { capitalize } from '@tymate/sergic';

const Wrapper = styled.div`
   background-color: #fff;
   min-height: 58px;
   box-shadow: 0 1px 0 #e0e4e8;
   padding: ${({ theme }) => theme.spacing()};
   line-height: 26px;
   z-index: 1;
   margin-top: -60px;
`;

const MembersList = styled(InlineList)`
   margin-left: ${({ theme }) => theme.spacing(-0.25)};
`;

const MembersListItem = styled.li`
   display: flex;
   align-items: center;
   margin-left: ${({ theme }) => theme.spacing(0.25)};
   margin-bottom: ${({ theme }) => theme.spacing(0.25)};
   background-color: ${({ theme }) => theme.primary};
   color: #fff;
   padding: 2px 4px;
   border-radius: ${({ theme }) => theme.borderRadius};
   font-size: ${({ theme }) => theme.size()};
`;

const DeleteMemberButton = styled(ButtonReset)`
   margin-left: ${({ theme }) => theme.spacing(0.5)};
   color: inherit;
   cursor: pointer;
   font-size: ${({ theme }) => theme.size(1)};
`;

const Input = styled.input`
   border: 0;
   outline: none;
   padding: 0;
   margin: 0;
   background-color: transparent;
   width: 100%;

   &::placeholder {
      color: ${({ theme }) => theme.textLighter};
   }
`;

const NewConversationMembers = ({
   focusOnMount = true,
   onDeleteEntity,
   onUpdateSearchInput,
   searchInputValue = '',
   entities,
   newConversationName = '',
   onChangeNewConversationName,
   onResize = () => {},
}) => {
   const { t } = useTranslation('newConversation');
   const [wrapperHeight, setWrapperHeight] = useState(0);
   const inputRef = useRef();
   const wrapperRef = useRef();
   const wrapperRefHeight = wrapperRef.current?.clientHeight;

   useEffectOnce(() => {
      if (focusOnMount) {
         inputRef.current.focus();
      }

      setWrapperHeight(wrapperRef.current.clientHeight);
   });

   useDeepCompareEffect(() => {
      if (wrapperRefHeight !== wrapperHeight) {
         setWrapperHeight(wrapperRefHeight);
         onResize(wrapperRefHeight);
      }
   }, [{ wrapperRefHeight, entities }]);

   return (
      <Wrapper ref={wrapperRef}>
         <Media>
            <MediaImage top style={{ lineHeight: entities.length > 0 ? 2 : 'inherit' }}>
               À :
            </MediaImage>
            <MediaBody>
               {entities.length > 0 && (
                  <MembersList>
                     {uniqBy(entities, ({ type, id }) => `${type}${id}`).map(({ id, displayName, type }) =>
                        type === 'DISTRIBUTION_LIST' ? (
                           <MembersListItem key={id}>
                              {capitalize(displayName)}
                              <DeleteMemberButton onClick={() => onDeleteEntity(id, 'DISTRIBUTION_LIST')}>
                                 <MdRemoveCircleOutline />
                              </DeleteMemberButton>
                           </MembersListItem>
                        ) : (
                           <MembersListItem key={id}>
                              {capitalize(displayName)}
                              <DeleteMemberButton onClick={() => onDeleteEntity(id, 'MEMBER')}>
                                 <MdRemoveCircleOutline />
                              </DeleteMemberButton>
                           </MembersListItem>
                        )
                     )}
                  </MembersList>
               )}

               <Input
                  value={searchInputValue}
                  onChange={onUpdateSearchInput}
                  placeholder={t('searchContact')}
                  ref={inputRef}
               />
            </MediaBody>
         </Media>

         <BorderedInput
            value={newConversationName}
            onChange={onChangeNewConversationName}
            size='small'
            style={{ marginTop: 8 }}
            variant='full'
            placeholder={t('searchConversation')}
            maxLength='60'
            required
         />
      </Wrapper>
   );
};

export default NewConversationMembers;
